/**
 * /map — NDVI redesign v1.3 (Sprint 2D).
 *
 * Заменяет Phase 5A структурный shell. Архитектура:
 *
 *   AppShell
 *     └─ MapRoot (relative)
 *         ├─ MapCanvas (absolute inset-0) — MapLibre + setupMapLayers
 *         ├─ CropFilterPanel (DraggablePanel: crops)
 *         ├─ AIInsightsPanel (DraggablePanel: ai)
 *         ├─ SatellitesPanel (DraggablePanel: satellites)
 *         ├─ CalibrationDrawer (Sheet, opens from satellites' "⚙ Калибровка")
 *         ├─ CompareDatesView (top-bar overlay when compare mode on)
 *         └─ FieldDetailPopover (when selectedFieldId !== null)
 *
 * Mobile (<768px): панели не draggable, стекаются под картой.
 *
 * Performance budget (spec):
 *   - mode switch <50ms (только visibility)
 *   - date switch <100ms (только setTiles)
 *   - field outline pop-out — never (decoupled architecture)
 */
import { useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { MapCanvas } from '@/components/map/MapCanvas';
import { BasemapSelector } from '@/components/map/BasemapSelector';
import { CropFilterPanel } from '@/components/map/CropFilterPanel';
import { AIInsightsPanel } from '@/components/map/AIInsightsPanel';
import { SatellitesPanelOrRestorePill } from '@/components/map/SatellitesPanel';
import { MapVerticalLegend } from '@/components/map/MapVerticalLegend';
import { CalibrationDrawer } from '@/components/map/CalibrationDrawer';
import { CompareDatesView } from '@/components/map/CompareDatesView';
import {
  FieldDetailPopover,
  type FieldDetail,
} from '@/components/map/FieldDetailPopover';
import { useMapStore } from '@/lib/map/useMapStore';
import { useAvailableDates } from '@/lib/map/useAvailableDates';
import { useIsMobile } from '@/lib/useBreakpoint';

export function MapPageLegacy() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const selectedFieldId = useMapStore((s) => s.selectedFieldId);
  const setSelectedFieldId = useMapStore((s) => s.setSelectedFieldId);
  // AK-71: scope-aware available dates — при выборе хозяйства/кластера показываем
  // только те снимки где есть scenes для этих полей.
  // AK-77: список зависит от текущего mode — для GNR пайплайн иногда не
  // генерирует GNR.cog (только NDVI), поэтому держим два независимых списка
  // и активный пробрасываем в SatellitesPanel/CompareDatesView/MapCanvas.
  const activeScope = useMapStore((s) => s.activeScope);
  const mode = useMapStore((s) => s.mode);
  const ndviDates = useAvailableDates({ indexCode: 'ndvi', scope: activeScope });
  const gnrDates = useAvailableDates({ indexCode: 'gnr', scope: activeScope });
  const availableDates =
    (mode === 'gnr' ? gnrDates : ndviDates) ?? undefined;

  // Кэшируем последнюю кликнутую feature (popover показывается рядом с курсором)
  const [activeField, setActiveField] = useState<FieldDetail | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ x: number; y: number } | undefined>(undefined);

  const handleFieldClick = useCallback(
    (field: FieldDetail, screenPos: { x: number; y: number }) => {
      setActiveField(field);
      setPopoverPos(screenPos);
    },
    [],
  );

  const handleAskAI = useCallback((field: FieldDetail) => {
    // TODO: интегрировать с AIAssistant — pre-filled query про поле.
    console.log('[map] Ask AI about field', field.id);
  }, []);

  const handleOpenDetail = useCallback(
    (fieldId: string) => {
      setSelectedFieldId(null);
      setActiveField(null);
      void navigate({ to: '/fields/$fieldId', params: { fieldId } });
    },
    [navigate, setSelectedFieldId],
  );

  // На mobile — стекаем панели в колонку под картой.
  if (isMobile) {
    return (
      <AppShell>
        <div className="flex flex-col gap-2 -mx-3 -my-3 h-[calc(100%+24px)]">
          <div className="relative h-[55vh] w-full">
            <MapCanvas onFieldClick={handleFieldClick} />
            <BasemapSelector />
            <MapVerticalLegend />
            {selectedFieldId && activeField && (
              <FieldDetailPopover
                field={activeField}
                position={popoverPos}
                onAskAI={handleAskAI}
                onOpenDetail={handleOpenDetail}
              />
            )}
          </div>
          <div className="px-2 space-y-2 overflow-y-auto pb-3">
            <AIInsightsPanel mobile onSelectField={(id) => setSelectedFieldId(id)} />
            <SatellitesPanelOrRestorePill mobile availableDates={availableDates} />
            <CropFilterPanel mobile />
          </div>
          <CalibrationDrawer />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {/* -m-6 + 100%+48px — escape main's p-6 чтобы карта заняла всю площадь.
          MapCanvas внутри = absolute inset-0. ResizeObserver в MapCanvas
          автоматически triggers map.resize() при изменении container size,
          что критично для предотвращения 500x500-рендера при flex layout race. */}
      <div className="relative -m-6 h-[calc(100%+48px)] w-[calc(100%+48px)]">
        <MapCanvas onFieldClick={handleFieldClick} />

        <BasemapSelector />

        <CropFilterPanel />
        <AIInsightsPanel onSelectField={(id) => setSelectedFieldId(id)} />
        <SatellitesPanelOrRestorePill availableDates={availableDates} />
        <MapVerticalLegend />

        <CompareDatesView availableDates={availableDates ?? []} />

        {selectedFieldId && activeField && (
          <FieldDetailPopover
            field={activeField}
            position={popoverPos}
            onAskAI={handleAskAI}
            onOpenDetail={handleOpenDetail}
          />
        )}

        <CalibrationDrawer />
      </div>
    </AppShell>
  );
}

export default MapPageLegacy;
